import { MarkdownPreview } from '@consigli/facade';
import {
  useGetReportTemplateDownloadUrlQuery,
  useGetReportTemplateQuery,
  useReportTemplateFileUpload,
  useUpdateReportTemplateMutation,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { Field, SheetForm, TextArea } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { TemplateMetadataFields } from './components';

interface EditTemplateFormProps {
  templateId: string;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
}

export const EditTemplateForm: FC<EditTemplateFormProps> = ({
  templateId,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onSuccess,
}) => {
  const { data: template, isLoading: isLoadingTemplate } = useGetReportTemplateQuery({
    id: templateId,
  });

  // Get authenticated download URL with SAS token for markdown files
  // This endpoint generates a time-limited SAS token for secure blob access
  const {
    data: downloadUrlData,
    isLoading: isLoadingDownloadUrl,
    error: downloadUrlError,
  } = useGetReportTemplateDownloadUrlQuery(
    { id: templateId },
    {
      skip: !template || template.fileExtension !== '.md',
      refetchOnMountOrArgChange: false,
    },
  );

  const [updateTemplate, { isLoading: isUpdating }] = useUpdateReportTemplateMutation();
  const { overwrite } = useReportTemplateFileUpload();

  const [isOpen, setIsOpen] = useState(controlledOpen ?? false);
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState('');
  const [projectType, setProjectType] = useState('');
  const [location, setLocation] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [originalFileContent, setOriginalFileContent] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isPreviewMode, setIsPreviewMode] = useState(true);

  const fetchFileContent = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const content = await response.text();
        setFileContent(content);
        setOriginalFileContent(content);
      } else if (response.status === 404) {
        errorToast({
          message:
            'File not found in Azure Storage. The file may not have been uploaded successfully.',
        });
        console.error('File not found:', url);
      } else {
        errorToast({ message: `Failed to load file: ${response.status} ${response.statusText}` });
        console.error('Fetch error:', response.status, response.statusText, url);
      }
    } catch (err) {
      console.error('Failed to fetch file content:', err, 'URL:', url);
      errorToast({ message: 'Failed to load file content. Check console for details.' });
    }
  }, []);

  // Load template data when it becomes available
  useEffect(() => {
    if (template) {
      setName(template.name);
      setReportType(template.reportType);
      setProjectType(template.projectType);
      setLocation(template.location);
    }
  }, [template]);

  // Fetch markdown content using authenticated download URL with SAS token
  useEffect(() => {
    if (template?.fileExtension === '.md') {
      if (downloadUrlData?.downloadUrl) {
        // Use authenticated download URL with SAS token
        fetchFileContent(downloadUrlData.downloadUrl);
      } else if (!isLoadingDownloadUrl && downloadUrlError) {
        // Show error if download URL endpoint fails
        const errorMsg =
          'Failed to get secure download URL. Azure Storage credentials may not be configured in backend.';
        console.error('❌ Download URL error:', downloadUrlError);
        setError(errorMsg);
        errorToast({ message: errorMsg });
      }
      // If isLoadingDownloadUrl is true, wait for it to finish
    }
  }, [template, downloadUrlData, isLoadingDownloadUrl, downloadUrlError, fetchFileContent]);

  // Sync controlled open state
  useEffect(() => {
    if (controlledOpen !== undefined) {
      setIsOpen(controlledOpen);
    }
  }, [controlledOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);

      if (!open) {
        // Reset form when closing
        setError(undefined);
      }
    },
    [onOpenChange],
  );

  const handleSubmit = async (): Promise<boolean> => {
    if (!template) return false;

    // Validation
    if (!name || !reportType || !projectType || !location) {
      setError('Please fill in all required fields');
      return false;
    }

    setError(undefined);

    try {
      // Step 1: Overwrite the file in Azure Blob Storage if content changed
      if (fileContent !== originalFileContent) {
        try {
          await overwrite(fileContent, templateId);
        } catch (overwriteErr) {
          console.error('Failed to overwrite file in Azure:', overwriteErr);
          throw new Error(
            'Failed to save file content to Azure Storage. CORS may not be configured.',
          );
        }
      }
      // If content unchanged, just update metadata below

      // Step 2: Update the database record with new metadata
      await updateTemplate({
        id: templateId,
        data: {
          name,
          report_type: reportType,
          project_type: projectType,
          location,
          file_name: template.fileName,
          // file_url stays the same since we're overwriting at the same location
        },
      }).unwrap();

      successToast({ message: 'Template updated successfully' });
      handleOpenChange(false);
      onSuccess?.();
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update template';
      setError(errorMessage);
      errorToast({ message: errorMessage });
      return false;
    }
  };

  const handleCancel = () => {
    if (template) {
      setName(template.name);
      setReportType(template.reportType);
      setProjectType(template.projectType);
      setLocation(template.location);
      setError(undefined);
    }
  };

  if (isLoadingTemplate) {
    return null;
  }

  if (!template) {
    return null;
  }

  return (
    <SheetForm
      title={`Edit Template: ${template.name}`}
      description="Edit template metadata and content. Changes will overwrite the existing file."
      trigger={trigger}
      submitLabel={isUpdating ? 'Saving...' : 'Save Changes'}
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSubmitting={isUpdating}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Metadata Fields */}
        <TemplateMetadataFields
          name={name}
          reportType={reportType}
          projectType={projectType}
          location={location}
          onNameChange={setName}
          onReportTypeChange={setReportType}
          onProjectTypeChange={setProjectType}
          onLocationChange={setLocation}
          disabled={isUpdating}
          showError={!!error}
        />

        {/* Markdown Content Editor/Preview */}
        <Field htmlFor="file-content" label="Template Content (Markdown)">
          <div className="space-y-2">
            {/* Preview/Edit Toggle */}
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant={isPreviewMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsPreviewMode(true)}
                disabled={isUpdating}
              >
                Preview
              </Button>
              <Button
                type="button"
                variant={!isPreviewMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsPreviewMode(false)}
                disabled={isUpdating}
              >
                Edit
              </Button>
            </div>

            {/* Content Area */}
            {isPreviewMode ? (
              <div className="border rounded-md p-4 bg-gray-50 min-h-[400px] max-h-[600px] overflow-y-auto">
                {fileContent ? (
                  <MarkdownPreview content={fileContent} />
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No content to preview. Switch to Edit mode to add content.
                  </p>
                )}
              </div>
            ) : (
              <>
                <TextArea
                  id="file-content"
                  value={fileContent}
                  onChange={(e) => setFileContent(e.target.value)}
                  placeholder="# Template Title

## Section 1

Your template content here...

### Variables
- {{project_name}}
- {{location}}
- {{date}}
"
                  disabled={isUpdating}
                  rows={20}
                  fullWidth
                  className="font-mono text-sm"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Edit markdown content directly. Supports headers, lists, bold, italic, links,
                  tables, etc.
                </p>
              </>
            )}
          </div>
        </Field>

        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
          <p className="text-sm text-yellow-900">
            <strong>⚠️ Warning:</strong> This action is irreversible. Saving will permanently
            overwrite the existing template file and update its metadata. The previous version
            cannot be recovered.
          </p>
        </div>
      </div>
    </SheetForm>
  );
};
